import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { Info as InfoIcon } from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import { KeyboardDatePicker } from '@material-ui/pickers'
import clsx from 'clsx'
import { Formik } from 'formik'
import moment from 'moment'
import { SearchWidget } from 'pages/HomePage/components'
import { ROUTES } from 'routes'
import { CustomizedTooltip, Footer, MainLayout, NavBar, PageContainer, StyledSwitch } from 'shared'
import { setAdministratorAccount } from 'state/modules/app'
import { createChampionship } from 'state/modules/championships'
import {
  load as loadOrganization,
  loadReset as loadResetOrganization
} from 'state/modules/organizations'
import {
  ARGENTINA_COUNTRY,
  CHAMPIONSHIP_TYPES,
  MAX_DATE,
  MIN_DATE,
  OTHER_OPTION,
  STRING_EMPTY
} from 'utils/constants'
import { getCountries, searchDisciplines } from 'utils/functions'
import { useQuery } from 'utils/hooks'
import { showSnackbarSuccess } from 'utils/snackbar'
import * as Yup from 'yup'

import { PlaceholderLoader } from './components'
import { useStyles } from './CreatePage.style'

const MAX_ALLOWED_DISCIPLINES = 3
const INITIAL_DATE_FOR_CHAMPIONSHIP_CREATE = moment('01-01-2024')

const CreatePage = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const query = useQuery()
  const firstRender = useRef(true)

  const organizationIdOrHandle = query.get('organizationIdOrHandle')

  const { administratorAccount } = useSelector((state) => state.app)
  const { organization, pending } = useSelector((state) => state.organizations)
  const { createChampionshipPending } = useSelector((state) => state.championships)

  const [countries, setCountries] = useState([])
  const [disciplines, setDisciplines] = useState([])

  useEffect(() => {
    if (!countries.length) fetchCountries()
    if (!disciplines.length) fetchDisciplines()
  }, [administratorAccount])

  useEffect(() => {
    if (organizationIdOrHandle) dispatch(loadOrganization(organizationIdOrHandle))

    return () => {
      dispatch(loadResetOrganization())
    }
  }, [organizationIdOrHandle])

  useEffect(() => {
    if (organization && organization.loggedUserMetadata.canEdit)
      dispatch(setAdministratorAccount({ ...administratorAccount, organization }))
  }, [organization])

  useEffect(() => {
    firstRender.current = false
  }, [])

  const activeLoading = firstRender.current || pending

  const values = {
    name: STRING_EMPTY,
    description: STRING_EMPTY,
    disciplinesSelect: [],
    fromDate: null,
    toDate: null,
    eventsCount: STRING_EMPTY,
    computedEventsCount: STRING_EMPTY,
    type: STRING_EMPTY,
    countryId: administratorAccount.organization?.countryId ?? ARGENTINA_COUNTRY.value,
    lastEventAccreditationRequired: false,
    distanceAward: false
  }

  const validationSchema = Yup.object().shape(
    {
      name: Yup.string()
        .required('Debes ingresar el nombre del campeonato')
        .max(100, 'Máximo 100 caracteres')
        .trim(),
      description: Yup.string()
        .required('Debes ingresar una descripción del campeonato')
        .min(100, 'Mínimo 100 caracteres')
        .max(4000, 'Máximo 4000 caracteres')
        .trim(),
      type: Yup.string().required('Debes seleccionar el tipo de campeonato'),
      eventsCount: Yup.number()
        .positive('El número de eventos debe ser positivo')
        .integer('El número de eventos debe ser un número entero')
        .required('Ingrese el número de eventos totales del campeonato')
        .max(24, 'Máximo 24 eventos'),
      computedEventsCount: Yup.number()
        .positive('El número de eventos computables debe ser positivo')
        .integer('El número de eventos computables debe ser un número entero')
        .required('Ingrese el número de eventos computables del campeonato')
        .max(24, 'Máximo 24 eventos')
        .test(
          'less-than-eventsCount',
          'El número de eventos computables debe ser menor al total',
          (value, { parent }) => !parent.eventsCount || value <= parent.eventsCount
        ),
      disciplinesSelect: Yup.array()
        .required('Debes seleccionar al menos una disciplina')
        .min(1, 'Debes seleccionar al menos una disciplina')
        .max(MAX_ALLOWED_DISCIPLINES, 'Puede seleccionar hasta 3 disciplinas'),
      countryId: Yup.string().required('Debes seleccionar un país'),
      fromDate: Yup.mixed()
        .nullable()
        .required('Ingrese el año de inicio del campeonato')
        .test('valid-date', 'Formato inválida', (value) => moment(value).isValid())
        .test('range-date', 'Formato inválida', (value) =>
          moment(value).isBetween(moment(MIN_DATE), moment(MAX_DATE), 'YYYY-MM-DD')
        )
        .test('min-date', 'Podra escoger el año a partir de 2024', (value) =>
          moment(value).isSameOrAfter(INITIAL_DATE_FOR_CHAMPIONSHIP_CREATE, 'year')
        )
        .when('toDate', {
          is: (toDate) => toDate && moment(toDate).isValid(),
          then: (schema) =>
            schema
              .test(
                'date-compare',
                'El año de inicio no puede ser mayor al de fin',
                (value, { parent }) => moment(value).isBefore(moment(parent.toDate))
              )
              .test(
                'championship-duration',
                'El campeonato puede durar hasta 2 años',
                (value, { parent }) => moment(parent.toDate).diff(value, 'year') <= 1
              )
        }),
      toDate: Yup.mixed()
        .nullable()
        .required('Ingrese el año de cierre del campeonato')
        .test('valid-date', 'Formato inválida', (value) => moment(value).isValid())
        .test('range-date', 'Formato inválida', (value) =>
          moment(value).isBetween(moment(MIN_DATE), moment(MAX_DATE), 'YYYY-MM-DD')
        )
        .test('min-date', 'Podra escoger el año a partir de 2024', (value) =>
          moment(value).isSameOrAfter(INITIAL_DATE_FOR_CHAMPIONSHIP_CREATE, 'year')
        )
        .when('fromDate', {
          is: (fromDate) => fromDate && moment(fromDate).isValid(),
          then: (schema) =>
            schema.test(
              'date-compare',
              'El año de fin no puede ser menor al de inicio',
              (value, { parent }) => moment(value).isAfter(moment(parent.fromDate))
            )
        })
    },
    ['fromDate', 'toDate']
  )

  const onSubmit = async ({
    computedEventsCount,
    description,
    disciplinesSelect,
    eventsCount,
    fromDate,
    lastEventAccreditationRequired,
    name,
    toDate,
    type,
    countryId,
    distanceAward
  }) => {
    const payload = {
      computedEventsCount,
      description: description.trim(),
      disciplines: disciplinesSelect.map((x) => ({ id: x.value })),
      eventsCount,
      from: fromDate?.toISOString(),
      to: toDate?.toISOString(),
      lastEventAccreditationRequired,
      name: name.trim(),
      type,
      countryId,
      distanceAward,
      organizationId: administratorAccount.organization.id
    }

    const createSuccess = await dispatch(createChampionship(payload))
    if (createSuccess) {
      showSnackbarSuccess('El campeonato fue creado con éxito.')
      history.push(`${ROUTES.CHAMPIONSHIPS.PROFILE}/${createSuccess.slugUrl}`)
    }
  }

  const onDisciplinesChange = (setFieldValue) => (options) =>
    setFieldValue('disciplinesSelect', options)

  const fetchDisciplines = async () => setDisciplines(await searchDisciplines(STRING_EMPTY))
  const fetchCountries = async () => setCountries(await getCountries()(STRING_EMPTY))

  const CHAMPIONSHIP_TYPES_OPTIONS = Object.values(CHAMPIONSHIP_TYPES)

  const renderLoading = () => <PlaceholderLoader />

  const renderPage = () => {
    if (!organization) {
      return (
        <div className={classes.noPermissionContainer}>
          <Typography variant='h6' color='primary' className={classes.noPermissionTitle}>
            Ocurrió un error al cargar el formulario del campeonato.
          </Typography>
        </div>
      )
    }

    if (!organization.loggedUserMetadata?.canEdit) {
      return (
        <div className={classes.noPermissionContainer}>
          <Typography variant='h6' color='primary' className={classes.noPermissionTitle}>
            No tienes permisos para crear campeonatos con esta organización.
          </Typography>
        </div>
      )
    }

    return renderForm()
  }

  const renderForm = () => (
    <Formik
      enableReinitialize
      initialValues={values}
      validationSchema={validationSchema}
      onSubmit={onSubmit}>
      {({
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValid,
        setFieldValue
      }) => (
        <form onSubmit={handleSubmit} className={classes.form}>
          <div className={classes.bodyContainer}>
            <Typography variant='h6' className={classes.title} color='primary'>
              Crear campeonato para {administratorAccount.organization?.name || STRING_EMPTY}
            </Typography>
            <div className={classes.mainContainer}>
              <section className={classes.section}>
                <div className={classes.formGroup}>
                  <TextField
                    className={classes.input}
                    name='name'
                    autoComplete='off'
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={errors.name || 'Máximo 100 caracteres.'}
                    error={touched.name && Boolean(errors.name)}
                    fullWidth
                    label='Nombre del Campeonato'
                    required
                    variant='outlined'
                    size='small'
                    margin='none'
                  />
                </div>

                <div className={classes.formGroup}>
                  <div className={classes.disciplinesContainer}>
                    <FormControl variant='outlined'>
                      <Autocomplete
                        multiple
                        id='disciplinesSelect-autocomplete'
                        options={disciplines.filter((x) => x.label !== OTHER_OPTION.label)}
                        getOptionLabel={(option) => option.label}
                        selectOnFocus
                        onChange={(event, optionSelected) =>
                          onDisciplinesChange(setFieldValue)(optionSelected)
                        }
                        getOptionSelected={(x) =>
                          values.disciplinesSelect.some((o) => o.value === x.value)
                        }
                        filterSelectedOptions
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant='outlined'
                              label={option.label}
                              key={option.value}
                              title={option.label}
                              {...getTagProps({ index })}
                              color='primary'
                              className={classes.option}
                            />
                          ))
                        }
                        clearText='Quitar todo'
                        openText='Abrir desplegable'
                        closeText='Cerrar desplegable'
                        value={values.disciplinesSelect}
                        className={classes.combo}
                        loadingText={<Typography align='center'> Cargando...</Typography>}
                        noOptionsText={<Typography align='center'> Sin opciones</Typography>}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            className={clsx(classes.input, classes.comboInput)}
                            name='disciplinesSelect'
                            onBlur={handleBlur}
                            label='Seleccione Deporte/ Disciplina'
                            variant='outlined'
                            error={touched.disciplinesSelect && Boolean(errors.disciplinesSelect)}
                            helperText={
                              errors.disciplinesSelect || 'Escriba el nombre de la disciplina'
                            }
                          />
                        )}
                      />
                    </FormControl>
                    <CustomizedTooltip
                      position='right-end'
                      className={classes.tooltip}
                      disabledClickAway
                      arrowClassName={classes.tooltipArrow}
                      buttonClassName={classes.tooltipButton}
                      title={
                        <Typography className={classes.tooltipTitle}>
                          Si tu disciplina no figura en el listado y no es una subdisciplina de las
                          existentes, por favor envíanos un correo a:&nbsp;
                          <a
                            href='mailto:soporte@sportmetric.net'
                            target='_blank'
                            rel='noopener noreferrer'
                            className={classes.tooltipLink}>
                            soporte@sportmetric.net
                          </a>
                          &nbsp;indicándonos cuál es para que la podamos agregar.
                        </Typography>
                      }>
                      {({ handleTooltip }) => (
                        <IconButton
                          onClick={handleTooltip}
                          color='primary'
                          className={classes.infoButton}
                          size='small'>
                          <InfoIcon color='primary' />
                        </IconButton>
                      )}
                    </CustomizedTooltip>
                  </div>
                </div>

                <div className={classes.formGroup}>
                  <TextField
                    name='description'
                    value={values.description}
                    className={`${classes.input} multiline`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={
                      errors.description || 'Mínimo 100 caracteres máximo 4000 caracteres.'
                    }
                    error={touched.description && Boolean(errors.description)}
                    fullWidth
                    autoComplete='off'
                    multiline
                    rowsMax={7}
                    required
                    label='Descripción del Campeonato'
                    rows={1}
                    variant='outlined'
                    size='small'
                    margin='none'
                  />
                </div>

                <div className={classes.dateFormGroup}>
                  <Typography className={classes.date} color='primary' variant='h6'>
                    Año del campeonato*
                  </Typography>

                  <div className={classes.pickers}>
                    <div className={classes.datePickerFormGroup}>
                      <KeyboardDatePicker
                        className={classes.inputDatePicker}
                        format='YYYY'
                        autoOk
                        variant='inline'
                        name='fromDate'
                        required
                        label='Desde'
                        margin='none'
                        onChange={(e) => setFieldValue('fromDate', e ? e.startOf('year') : null)}
                        id='date-picker-inline'
                        inputVariant='outlined'
                        onBlur={handleBlur}
                        value={values.fromDate}
                        minDate={INITIAL_DATE_FOR_CHAMPIONSHIP_CREATE}
                        KeyboardButtonProps={{
                          'aria-label': 'seleccione fecha'
                        }}
                        helperText={STRING_EMPTY}
                        placeholder='aaaa'
                        views={['year']}
                        error={Boolean(errors.fromDate)}
                      />

                      {errors.fromDate && (
                        <Typography
                          color={touched.fromDate ? 'error' : 'inherit'}
                          className={classes.altErrorMessage}>
                          {errors.fromDate}
                        </Typography>
                      )}
                    </div>

                    <div className={classes.datePickerFormGroup}>
                      <KeyboardDatePicker
                        className={classes.inputDatePicker}
                        format='YYYY'
                        autoOk
                        variant='inline'
                        name='toDate'
                        required
                        label='Hasta'
                        margin='none'
                        onChange={(e) => setFieldValue('toDate', e ? e.endOf('year') : null)}
                        id='date-picker-inline'
                        inputVariant='outlined'
                        onBlur={handleBlur}
                        value={values.toDate}
                        minDate={INITIAL_DATE_FOR_CHAMPIONSHIP_CREATE}
                        views={['year']}
                        KeyboardButtonProps={{
                          'aria-label': 'seleccione fecha'
                        }}
                        placeholder='aaaa'
                        helperText={STRING_EMPTY}
                        error={touched.toDate && Boolean(errors.toDate)}
                      />

                      {errors.toDate && (
                        <Typography
                          color={touched.toDate ? 'error' : 'inherit'}
                          className={classes.altErrorMessage}>
                          {errors.toDate}
                        </Typography>
                      )}
                    </div>
                  </div>
                </div>
              </section>
              <section className={classes.section}>
                <div className={classes.formGroup}>
                  <FormControl variant='outlined' fullWidth>
                    <InputLabel
                      id='country-native-simple'
                      className={clsx(classes.labelSelect, errors.countryId && 'error')}>
                      País*
                    </InputLabel>
                    <Select
                      className={classes.select}
                      value={values.countryId}
                      required
                      fullWidth
                      error={Boolean(errors.countryId) && touched.countryId}
                      onBlur={handleBlur}
                      name='countryId'
                      onChange={(e) => setFieldValue('countryId', e.target.value)}
                      color='primary'
                      variant='outlined'
                      displayEmpty
                      labelId='country-native-simple'
                      label='País'>
                      {countries.map((x) => (
                        <MenuItem value={x.value} key={x.value}>
                          {x.label}
                        </MenuItem>
                      ))}
                      <MenuItem value={STRING_EMPTY} disabled></MenuItem>
                    </Select>
                    <Typography
                      variant='caption'
                      className={classes.errorMessage}
                      color={errors.countryId ? 'error' : 'inherit'}>
                      {errors.countryId || 'Seleccione su País.'}
                    </Typography>
                  </FormControl>
                </div>

                <div className={classes.formGroup}>
                  <div className={classes.otherContainer}>
                    <Typography variant='h6' className={classes.numberLabel}>
                      Eventos totales que conforman el Campeonato
                    </Typography>
                    <TextField
                      name='eventsCount'
                      value={values.eventsCount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder='-'
                      error={touched.eventsCount && Boolean(errors.eventsCount)}
                      autoComplete='off'
                      variant='outlined'
                      size='small'
                      InputLabelProps={{
                        shrink: true
                      }}
                      inputProps={{
                        step: 1,
                        min: 0,
                        max: 24
                      }}
                      type='number'
                      className={clsx(classes.input, classes.numericField)}
                      margin='none'
                    />
                  </div>
                  {errors.eventsCount && (
                    <Typography
                      color={touched.eventsCount ? 'error' : 'inherit'}
                      className={classes.altErrorMessage}>
                      {errors.eventsCount}
                    </Typography>
                  )}
                </div>
                <div className={classes.formGroup}>
                  <div className={classes.otherContainer}>
                    <Typography variant='h6' className={classes.numberLabel}>
                      Eventos computables del Campeonato
                    </Typography>
                    <TextField
                      name='computedEventsCount'
                      value={values.computedEventsCount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder='-'
                      error={touched.computedEventsCount && Boolean(errors.computedEventsCount)}
                      autoComplete='off'
                      variant='outlined'
                      size='small'
                      className={clsx(classes.input, classes.numericField)}
                      margin='none'
                      InputLabelProps={{
                        shrink: true
                      }}
                      inputProps={{
                        step: 1,
                        max: 24,
                        min: 0
                      }}
                      type='number'
                    />
                  </div>
                  {errors.computedEventsCount && (
                    <Typography
                      color={touched.computedEventsCount ? 'error' : 'inherit'}
                      className={classes.altErrorMessage}>
                      {errors.computedEventsCount}
                    </Typography>
                  )}
                </div>

                <div className={classes.formGroup}>
                  <div className={classes.otherContainer}>
                    <Typography variant='h6' className={classes.numberLabel}>
                      ¿Último evento obligatorio?
                    </Typography>

                    <div className={classes.switchContainer}>
                      <Typography variant='h6' className={classes.switchTitle}>
                        No
                      </Typography>
                      <StyledSwitch
                        checked={values.lastEventAccreditationRequired}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        name='lastEventAccreditationRequired'
                      />
                      <Typography variant='h6' className={classes.switchTitle}>
                        Si
                      </Typography>
                    </div>
                  </div>
                </div>

                <div className={classes.formGroup}>
                  <div className={classes.otherContainer}>
                    <div className={classes.distanceAwardContainer}>
                      <Typography variant='h6' className={classes.numberLabel}>
                        ¿Premiar por distancia?
                      </Typography>

                      <CustomizedTooltip
                        position='right-end'
                        className={classes.tooltip}
                        disabledClickAway
                        arrowClassName={classes.tooltipArrow}
                        buttonClassName={classes.tooltipButton}
                        title={
                          <Typography className={classes.tooltipTitle}>
                            Para usar esta opción Todos los eventos del campeonato deben tener las
                            mismas distancias
                          </Typography>
                        }>
                        {({ handleTooltip }) => (
                          <IconButton color='primary' size='small' onClick={handleTooltip}>
                            <InfoIcon />
                          </IconButton>
                        )}
                      </CustomizedTooltip>
                    </div>

                    <div className={classes.switchContainer}>
                      <Typography variant='h6' className={classes.switchTitle}>
                        No
                      </Typography>
                      <StyledSwitch
                        checked={values.distanceAward}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        name='distanceAward'
                      />
                      <Typography variant='h6' className={classes.switchTitle}>
                        Si
                      </Typography>
                    </div>
                  </div>
                </div>

                <div className={classes.formGroup}>
                  <FormControl fullWidth variant='outlined' className={classes.typeFormGroup}>
                    <InputLabel
                      id='championship-simple'
                      className={clsx(classes.labelSelect, touched.type && errors.type && 'error')}>
                      Seleccione el Tipo de Campeonato*
                    </InputLabel>
                    <Select
                      className={classes.select}
                      value={values.type}
                      required
                      fullWidth
                      error={Boolean(errors.type) && touched.type}
                      onBlur={handleBlur}
                      name='type'
                      onChange={handleChange}
                      color='primary'
                      variant='outlined'
                      labelId='championship-simple'
                      label='Seleccione el Tipo de Campeonato*'>
                      {CHAMPIONSHIP_TYPES_OPTIONS.map((x) => (
                        <MenuItem value={x.value} key={x.value}>
                          {x.label}
                        </MenuItem>
                      ))}
                    </Select>

                    {errors.type && (
                      <Typography
                        color={touched.type ? 'error' : 'inherit'}
                        className={classes.altErrorMessage}>
                        {errors.type}
                      </Typography>
                    )}
                  </FormControl>
                </div>
              </section>
            </div>
          </div>

          <Button
            color='primary'
            variant='contained'
            type='submit'
            className={classes.createButton}
            disabled={!isValid || isSubmitting}
            endIcon={createChampionshipPending && <CircularProgress size={16} color='primary' />}>
            Crear Campeonato
          </Button>
        </form>
      )}
    </Formik>
  )

  return (
    <MainLayout>
      <NavBar widget={(props) => <SearchWidget mini {...props} />} altMode />
      <PageContainer customContainerClassName={classes.container}>
        {activeLoading ? renderLoading() : renderPage()}
      </PageContainer>
      <Footer />
    </MainLayout>
  )
}

export default CreatePage
